<template>
  <section v-if="sector" class="min-h-screen bg-gradient-to-br from-gray-50 to-green-50 text-gray-900 overflow-hidden">
    <!-- Animated particles background -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none z-0">
      <div v-for="i in 15" :key="i" 
           class="absolute rounded-full bg-green-100/20 animate-float"
           :class="`w-${Math.floor(Math.random()*15)+8} h-${Math.floor(Math.random()*15)+8}`"
           :style="`top:${Math.random()*100}%;left:${Math.random()*100}%;animation-duration:${Math.random()*20+10}s;animation-delay:${Math.random()*5}s`">
      </div>
    </div>

    <div class="container mx-auto px-4 sm:px-6 py-16 md:py-24 relative z-10">
      <!-- Hero Section -->
      <div class="relative overflow-hidden rounded-3xl shadow-2xl bg-gradient-to-r from-green-600/90 to-green-800/90 backdrop-blur-md border border-white/10 text-white mb-16 md:mb-24">
        <div class="absolute inset-0 z-0">
          <img :src="sector.image" 
               class="w-full h-full object-cover opacity-20" 
               :style="{transform:`scale(${1+parallax*0.05})`}"/>
        </div>
        <div class="relative z-10 p-8 sm:p-12 md:p-16 lg:p-20 space-y-4 md:space-y-6">
          <div class="flex items-center space-x-3 animate-fade-in">
            <span class="px-3 py-1 bg-white/20 rounded-full text-xs sm:text-sm font-medium backdrop-blur-sm">
              {{sector.name}}
            </span>
          </div>
          <h1 class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl">
            <span class="bg-clip-text text-transparent bg-gradient-to-r from-white to-green-200">
              Transformer l'avenir du CVC
            </span>
          </h1>
          <p class="text-lg sm:text-xl md:text-2xl text-blue-100/90 max-w-3xl leading-relaxed">
            {{sector.shortDescription}}
          </p>
          <button @click="scrollToContent" 
                  class="mt-6 px-6 py-2 sm:px-8 sm:py-3 bg-white text-green-600 font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 group">
            <span>Explorer</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 animate-bounce group-hover:animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Content Sections - Optimized for no white space -->
      <div v-for="(content,index) in sector.sections" :key="index" 
           class="mb-16 md:mb-24 scroll-mt-20" :id="`section-${index}`">
        <div :class="[
          'group relative flex flex-col md:flex-row w-full overflow-hidden rounded-3xl shadow-xl bg-white border border-gray-100',
          index%2===0?'md:flex-row':'md:flex-row-reverse'
        ]">
          <!-- Image Column -->
          <div class="relative z-10 w-full md:w-1/2 aspect-video md:aspect-auto">
            <div class="absolute inset-0 bg-gradient-to-br from-green-50/20 to-transparent z-10"></div>
            <img :src="content.image" 
                 class="w-full h-full object-cover min-h-[300px] md:min-h-[450px]"
                 :style="{transform:`scale(${1+imageScales[index]*0.05})`}"
                 loading="lazy"
                 :alt="content.title"/>
          </div>
          
          <!-- Text Column -->
          <div class="relative z-10 w-full md:w-1/2 p-6 sm:p-8 md:p-10 lg:p-12 flex flex-col justify-center">
            <div class="space-y-4 sm:space-y-5 md:space-y-6 transform transition-all duration-700 ease-out" 
                 :style="{opacity:sectionOpacities[index],transform:`translateY(${sectionTranslates[index]}px)`}">
              <div class="flex items-center gap-3">
                <span class="text-green-600 font-bold text-base sm:text-lg">0{{index+1}}</span>
                <div class="h-px bg-green-600 flex-1"></div>
              </div>
              <h3 class="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 leading-tight">
                {{content.title}}
              </h3>
              <p class="text-base sm:text-lg text-gray-600 leading-relaxed">
                {{content.description}}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA Section -->
      <div class="relative rounded-3xl overflow-hidden shadow-2xl border border-white/20 mt-16 md:mt-24">
        <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
        <img :src="sector.contactImage" 
             class="absolute inset-0 w-full h-full object-cover"
             :style="{transform:`translateY(${parallax*0.2}px)`}"
             loading="lazy"
             alt="Contactez-nous"/>
        
        <div class="relative z-10 p-8 sm:p-12 md:p-16 lg:p-20 text-white">
          <div class="max-w-2xl space-y-4 sm:space-y-6">
            <h3 class="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
              Discutons-en
            </h3>
            <p class="text-lg sm:text-xl text-green-100/90 leading-relaxed">
              Prêt à faire évoluer votre système CVC ? Planifiez une consultation et collaborons pour créer un système optimisé pour l'avenir.
            </p>
            
            <div class="flex flex-wrap gap-3 sm:gap-4 mt-6 sm:mt-8">
              <!-- routerview href='/ContactP' -->
              <button 
                @click="navigateToContact"
                class="px-6 py-3 sm:px-8 sm:py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-full shadow-lg transition-all duration-300 hover:scale-105 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                <span>Contactez-nous</span>
              </button>
              
              
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Back to Top Button -->
    <button @click="scrollToTop" 
            class="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 z-50"
            :class="{'opacity-0 translate-y-10':!showScrollButton,'opacity-100 translate-y-0':showScrollButton}"
            aria-label="Back to top">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
      </svg>
    </button>
  </section>

  <!-- 404 State -->
  <section v-else class="min-h-screen flex items-center justify-center p-6">
    <div class="text-center max-w-md">
      <h1 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">Secteur non trouvé</h1>
      <p class="text-lg sm:text-xl text-gray-600 mb-6">Le secteur que vous recherchez n'existe pas ou a été déplacé.</p>
      <router-link to="/" class="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow transition-all duration-300 hover:scale-105">
        Retour à l'accueil
      </router-link>
    </div>
  </section>
</template>

<script setup>
import {ref,onMounted,onUnmounted,computed} from 'vue'
import {useRoute, useRouter} from 'vue-router'  // Import both useRoute and useRouter

const route = useRoute()  // For accessing route params
const router = useRouter()  // For navigation

const navigateToContact = () => {
  router.push('/ContactP')  // Now using the correct router instance
}
const showScrollButton=ref(false)
const parallax=ref(0)
const sectionOpacities=ref([])
const sectionTranslates=ref([])
const imageScales=ref([])

const sectorList = [
  {
    name: 'Santé',
    slug: '1',
    image: 'https://futurehealthcaretoday.com/wp-content/uploads/2023/01/shutterstock_1101930905-scaled.jpg',
    shortDescription: "Des environnements de soins optimisés grâce à une régulation de l'air intelligente et un confort thermique assuré pour chaque patient.",
    sections: [
      {
        title: "Qualité de l'air améliorée",
        description: "Systèmes de filtration HEPA et contrôle automatisé pour limiter les infections et garantir un air purifié 24/7.",
        image: "https://cdn.pixabay.com/photo/2016/04/19/13/22/hospital-1338585_1280.jpg"
      },
      {
        title: "Confort patient personnalisé",
        description: "Zones de température réglables pour un bien-être optimal et une récupération accélérée.",
        image: "https://cdn.pixabay.com/photo/2016/09/02/16/17/dentist-1639683_1280.jpg"
      },
      {
        title: "Maintenance prédictive",
        description: "Capteurs IoT pour anticiper les besoins d'entretien et éviter les pannes critiques.",
        image: "https://cdn.pixabay.com/photo/2017/10/04/09/56/laboratory-2815641_1280.jpg"
      }
    ],
    contactImage: "https://www.shutterstock.com/image-photo/website-page-contact-us-email-260nw-2030413427.jpg",
  },
  {
    name: 'Industrie',
    slug: '2',
    image: 'https://cdn.corporatefinanceinstitute.com/assets/Industry-1024x576.jpeg',
    shortDescription: "Des solutions CVC robustes pour maximiser la productivité et réduire la consommation énergétique dans les usines.",
    sections: [
      {
        title: "Refroidissement industriel efficace",
        description: "Technologies de refroidissement pour maintenir des conditions stables, même dans des environnements extrêmes.",
        image: "https://www.solerpalau.com/blog/fr-fr/wp-content/uploads/sites/2/2021/12/efficacite-energetique-batiment.jpg"
      },
      {
        title: "Contrôle intelligent",
        description: "Automatisation complète pour gérer l'énergie et optimiser la production.",
        image: "https://www.elkarkour.com/wp-content/uploads/2024/10/image-25.jpeg"
      }
    ],
    contactImage: "https://www.shutterstock.com/image-photo/website-page-contact-us-email-260nw-2030413427.jpg",
  },
  {
    name: 'Hôtels & Resorts',
    slug: '3',
    image: 'https://imageio.forbes.com/specials-images/imageserve/6426e73a094eb28ac98cd6f1//960x0.jpg?height=473&width=711&fit=bounds',
    shortDescription: "Une atmosphère haut de gamme pour le confort et la satisfaction des clients, tout en minimisant la consommation d'énergie.",
    sections: [
      {
        title: "Chambres intelligentes",
        description: "Contrôle automatique de la température et de l'humidité selon les préférences de chaque invité.",
        image: "https://www.tiscontrol.com/tp_Public/Home/img/solutions/smart_hotel/hotel13.jpg"
      },
      {
        title: "Zones communes optimisées",
        description: "Une qualité de l'air parfaite dans les halls, restaurants et spas.",
        image: "https://www.yonder.fr/sites/default/files/styles/lg-insert/public/contenu/destinations/102-Amanyangyun-Dining_ARVA%C2%A9Aman.jpg?itok=3LVGICOO"
      }
    ],
    contactImage: "https://www.shutterstock.com/image-photo/website-page-contact-us-email-260nw-2030413427.jpg",
  },
  {
    name: 'Aéronautique & Transport',
    slug: '4',
    image: 'https://industries.ma/wp-content/uploads/2024/02/Transport-A%C3%A9rien.jpg',
    shortDescription: "Des solutions de contrôle climatique ultraperformantes pour l'aviation et les moyens de transport modernes.",
    sections: [
      {
        title: "Cabine confort",
        description: "Température stable et renouvellement de l'air même à haute altitude.",
        image: "https://www.lightzoomlumiere.fr/wp-content/uploads/2016/04/1-Airbus-Airspace-by-Airbus-A330neo-Ambiance%C2%A9Airbus.jpg"
      },
      {
        title: "Maintenance automatisée",
        description: "Systèmes de diagnostic pour réduire les coûts d'entretien des équipements climatiques embarqués.",
        image: "https://www.novae-recrute.com/images/mecanicien-aeronautique.jpg"
      }
    ],
    contactImage: "https://www.shutterstock.com/image-photo/website-page-contact-us-email-260nw-2030413427.jpg",
  },
  {
    name: 'Centres de Données',
    slug: '5',
    image: 'https://www.cisco.com/c/dam/en/us/solutions/collateral/data-center-virtualization/hyperflex-hyperconverged-infrastructure/nb-06-hyperflex-data-center-ebook-cth-2600-0217-en.jpg',
    shortDescription: "Des solutions de refroidissement de précision pour protéger les serveurs et éviter toute surchauffe.",
    sections: [
      {
        title: "Refroidissement de précision",
        description: "Flux d'air directs et contrôle d'humidité pour une performance serveur maximale.",
        image: "https://www.datacenterdynamics.com/media/16993/cooling.jpg"
      },
      {
        title: "Surveillance en temps réel",
        description: "Capteurs connectés pour un monitoring 24/7.",
        image: "https://www.vertiv.com/globalassets/products/thermal-management/vertiv-liebert-dse-freecooling.jpg"
      }
    ],
    contactImage: "https://st.depositphotos.com/1000423/2285/i/450/depositphotos_22859700-stock-photo-contact-us.jpg",
  }
];


const sector=computed(()=>sectorList.find((s)=>s.slug===route.params.slug)||null)

onMounted(()=>{
  if(sector.value){
    initializeAnimations()
    window.addEventListener('scroll',handleScroll)
  }
})

onUnmounted(()=>{
  window.removeEventListener('scroll',handleScroll)
})

const initializeAnimations=()=>{
  const sections=sector.value.sections||[]
  sectionOpacities.value=Array(sections.length).fill(0)
  sectionTranslates.value=Array(sections.length).fill(20)
  imageScales.value=Array(sections.length).fill(0)
}

const handleScroll=()=>{
  showScrollButton.value=window.scrollY>300
  parallax.value=window.scrollY*0.1
  
  sector.value.sections.forEach((_,index)=>{
    const element=document.getElementById(`section-${index}`)
    if(element){
      const rect=element.getBoundingClientRect()
      const isVisible=rect.top<window.innerHeight*0.75&&rect.bottom>0
      if(isVisible){
        sectionOpacities.value[index]=1
        sectionTranslates.value[index]=0
        imageScales.value[index]=1
      }
    }
  })
}

const scrollToContent=()=>{
  const firstSection=document.getElementById('section-0')
  firstSection?.scrollIntoView({behavior:'smooth'})
}

const scrollToTop=()=>{
  window.scrollTo({top:0,behavior:'smooth'})
}
</script>

<style>
@keyframes fade-in{
  from{opacity:0;transform:translateY(20px)}
  to{opacity:1;transform:translateY(0)}
}
@keyframes float{
  0%,100%{transform:translateY(0) rotate(0deg)}
  50%{transform:translateY(-20px) rotate(2deg)}
}
.animate-fade-in{
  animation:fade-in 1s ease-out forwards;
}
.animate-float{
  animation:float infinite linear;
}
::-webkit-scrollbar{
  width:8px;
  height:8px;
}
::-webkit-scrollbar-track{
  background:rgba(0,0,0,0.05);
  border-radius:10px;
}
::-webkit-scrollbar-thumb{
  background:rgba(59,130,246,0.5);
  border-radius:10px;
}
::-webkit-scrollbar-thumb:hover{
  background:rgba(59,130,246,0.7);
}
.transition-all{
  transition-property:all;
  transition-timing-function:cubic-bezier(0.4,0,0.2,1);
  transition-duration:300ms;
}
.bg-gradient-text{
  background-clip:text;
  -webkit-background-clip:text;
  color:transparent;
}
</style>